import React, { useMemo, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

export default function ImpulseTraderGame({ gameState = {}, gamePlayers = [], user = {}, socket, roomId, onGameAction }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const [pendingAction, setPendingAction] = useState(null);

  const state = useMemo(() => gameState || {}, [gameState]);
  const currentItem = state.currentItem || null;
  const playerId = user?.socketId || user?.userId || null;
  const playerState = playerId ? state.playerStates?.[playerId] : null;
  const phase = state.phase || 'playing';
  const finished = !!state.finished;
  const result = state.result || null;

  const dispatchAction = async (action) => {
    setPendingAction(action);
    try {
      if (typeof onGameAction === 'function') {
        await onGameAction({ type: 'take-action', action });
      } else {
        await submitAction({ type: 'take-action', action });
      }
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <div className="w-full rounded-[28px] border border-slate-200 bg-white p-4 text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
      <div className="mb-4 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70">
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <div className="rounded-full bg-emerald-100 px-3 py-1 font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
            Cash: £{playerState?.cash ?? state.startingCash ?? 100}
          </div>
          <div className="rounded-full bg-amber-100 px-3 py-1 font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
            Inventory: {playerState?.inventory?.length ?? 0}
          </div>
          <div className="rounded-full bg-sky-100 px-3 py-1 font-semibold text-sky-700 dark:bg-sky-900/40 dark:text-sky-200">
            {finished ? 'Finished' : `${state.roundTimeLeft ?? state.durationSeconds ?? 30}s left`}
          </div>
        </div>

        

        <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-950">
          <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            <span>Current item</span>
            <span>{currentItem?.icon || '📦'}</span>
          </div>
          <div className="mt-3 text-3xl font-semibold">{currentItem?.name || 'Loading...'}</div>
          <div className="mt-3 flex items-end gap-2">
            <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">£{currentItem?.price ?? 0}</div>
            {(() => {
              const currentPrice = Number(currentItem?.price ?? 0);
              const previousPrice = Number(currentItem?.previousPrice ?? currentPrice);
              const delta = currentPrice - previousPrice;
              const deltaDisplay = delta > 0 ? `+£${delta}` : delta < 0 ? `-£${Math.abs(delta)}` : 'flat';
              const deltaClass = delta > 0 ? 'text-emerald-600 dark:text-emerald-300' : delta < 0 ? 'text-rose-600 dark:text-rose-300' : 'text-slate-500 dark:text-slate-400';
              return <div className={`text-sm font-semibold ${deltaClass}`}>{deltaDisplay}</div>;
            })()}
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {['buy', 'sell'].map((action) => (
            <button
              key={action}
              onClick={() => dispatchAction(action)}
              disabled={pendingAction !== null || finished}
              className={`rounded-2xl px-4 py-4 text-lg font-semibold capitalize transition ${
                action === 'buy'
                  ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                  : 'bg-amber-500 text-white hover:bg-amber-400'
              } disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {pendingAction === action ? '…' : action}
            </button>
          ))}
        </div>

        {state.activeEvent ? (
          <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200">
            {state.activeEvent.label}
          </div>
        ) : null}

        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/70">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Inventory</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {(playerState?.inventory || []).slice(-8).map((entry, index) => {
              const currentValue = Number(entry?.currentPrice ?? entry?.purchasePrice ?? 0);
              const delta = Number(entry?.delta || 0);
              const deltaText = delta > 0 ? `+£${delta}` : delta < 0 ? `-£${Math.abs(delta)}` : 'flat';
              const deltaClass = delta > 0 ? 'text-emerald-600 dark:text-emerald-300' : delta < 0 ? 'text-rose-600 dark:text-rose-300' : 'text-slate-500 dark:text-slate-400';
              return (
                <span key={`${entry?.name ?? 'item'}-${index}`} className="rounded-full bg-white px-3 py-1 text-sm shadow-sm dark:bg-slate-900">
                  <span className="mr-1">{entry?.icon || '📦'}</span>
                  <span>{entry?.name || 'Item'}</span>
                  <span className="ml-1 font-semibold text-slate-700 dark:text-slate-200">£{currentValue}</span>
                  <span className={`ml-1 font-semibold ${deltaClass}`}>({deltaText})</span>
                </span>
              );
            })}
            {!(playerState?.inventory || []).length ? (
              <span className="text-sm text-slate-500 dark:text-slate-400">Nothing yet — buy an item to start building a stack.</span>
            ) : null}
          </div>
        </div>
      </div>

      {finished && result ? (
        <div className="mt-4 rounded-3xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-950/40">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">Results</div>
          <div className="mt-3 space-y-3">
            {(result.breakdown || []).map((entry) => {
              const totalValue = Number(entry.cash || 0) + Number(entry.inventoryValue || 0);
              return (
                <div key={entry.socketId} className="rounded-2xl border border-slate-200 bg-white p-3 text-sm dark:border-slate-700 dark:bg-slate-900">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{entry.username || entry.socketId}</span>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${entry.socketId === result.winners?.[0] ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'}`}>
                      {entry.socketId === result.winners?.[0] ? 'Winner' : 'Runner-up'}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600 dark:text-slate-300">
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 dark:bg-slate-800">Cash £{entry.cash ?? 0}</span>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 dark:bg-slate-800">Inventory £{entry.inventoryValue ?? 0}</span>
                    <span className="rounded-full bg-emerald-100 px-2.5 py-1 font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">Total £{totalValue}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="mt-4 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">How it works</div>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-slate-600 dark:text-slate-300">
          <li>Every tick, the price shifts and rare market events change the next move.</li>
          <li>Buy when the price is attractive, Sell when it’s profitable.</li>
          <li>At the end, each player’s total is cash plus the current value of their inventory.</li>
        </ul>
      </div>
    </div>
  );
}

ImpulseTraderGame.Guide = function ImpulseTraderGuide() {
  return (
    <div>
      <h3 className="mb-2 font-bold">Impulse Trader</h3>
      <p>Buy and sell as prices swing every second. Market events can turn a safe trade into a huge win or a painful loss.</p>
    </div>
  );
};
